import React, { useEffect, useMemo, useState } from "react";
import { useGroupChatContext } from "../../context/groupchatContext";
import HeaderChat from "./header";
import NoChatMessage from "./noChatMessage";
import ChatSidebar from "../sidebar/sidebar";
import MainPageDialog from "../../components/dialog";
import ProblemReportForm from "../problemReport/form";
import MessageThreadHeader from "./messageHeader";
import MessageThreadFooter from "./messageFooter";
import ChatWindow from "./chatWindow";
import PatientInfo from "../patientInfo/patientInfo";
import LocationAppointment from "../appointment/locationAppointment";
import MedicationAllergy from "../patientInfo/medicationAllergy";
import ChatbotPopup from "../chatbot";
import IncomingReferral from "../referral/incomingReferral";
import { AppointmentsFollowUp } from "../appointment/followupAppointment";
import VezoCallWindow from "../vezosession/vezoCallWindow";
import PinCodeFourDigit from "../login/pincodeFourDigit";
import PrintMessage from "./printMessage";
import SettingsPage from "../settings/SettingsPage";
import EncounterList from '../encounter/encounterList';
import { isPatientChatMessage } from "../../helpers/utility";
import { initializeFoxit } from "../../utils/foxitManager";
import TwoFa from "../settings/twoFASetting";
import { useGetLocationConfigurationQuery } from "../../services/location_services";

const isEnabled = (value) => value == 1 || value === true;

// Setup reactive window.isFoxitLoaded
if (!window.hasOwnProperty('_isFoxitLoaded')) {
    let _isFoxitLoaded = {cssLoaded: false,scriptLoaded: false};
    Object.defineProperty(window, 'isFoxitLoaded', {
        get: function() {
            return _isFoxitLoaded;
        },
        set: function(value) {
            _isFoxitLoaded = value;
            window.dispatchEvent(new Event('foxitStatusChanged'));
            console.log('Foxit status changed to:', value);
        }
    });
}

const GroupChat = () => {
    useEffect(() => {
        // If Foxit is already fully loaded, return early
        if (window.isFoxitLoaded?.cssLoaded && window.isFoxitLoaded?.scriptLoaded) {
          return;
        }

        // Check if CSS is already loaded
        const existingCss = document.querySelector('link[href="./lib/UIExtension.css"]');
        if (!existingCss && !window.isFoxitLoaded?.cssLoaded) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = './lib/UIExtension.css';
          link.onload = () => {
            window.isFoxitLoaded = {cssLoaded: true, scriptLoaded: window?.isFoxitLoaded?.scriptLoaded};
            window.dispatchEvent(new Event('foxitStatusChanged'));
            console.log(window.isFoxitLoaded,"window.isFoxitLoaded")
          };
          link.onerror = (error) => {
              console.error('Error loading Foxit CSS:', error);
          };
          document.head.appendChild(link);
        }
        else{
          window.isFoxitLoaded = {cssLoaded: true, scriptLoaded: window?.isFoxitLoaded?.scriptLoaded};
          window.dispatchEvent(new Event('foxitStatusChanged'));
          console.log(window.isFoxitLoaded,"window.isFoxitLoaded")
        }
    
        // Check if script is already loaded
        const existingScript = document.querySelector('script[src="./lib/UIExtension.full.source.modified.js"]');
        if (!existingScript && !window.isFoxitLoaded?.scriptLoaded) {
          const script = document.createElement('script');
          script.src = './lib/UIExtension.full.source.modified.js';
          script.async = true;
      
          script.onload = () => {
            window.isFoxitLoaded = {cssLoaded: window?.isFoxitLoaded?.cssLoaded, scriptLoaded: true};
            initializeFoxit();
          };
      
          script.onerror = (error) => {
            console.error('Error loading Foxit script:', error);
          };
      
          document.body.appendChild(script);
        }
        else{
          window.isFoxitLoaded = {cssLoaded: window?.isFoxitLoaded?.cssLoaded, scriptLoaded: window?.isFoxitLoaded?.scriptLoaded};
          window.dispatchEvent(new Event('foxitStatusChanged'));
          console.log(window.isFoxitLoaded,"window.isFoxitLoaded")
        }
    
        return () => {
          // Only remove resources if component is being fully unmounted
          const foxitScript = document.querySelector('script[src="./lib/UIExtension.full.source.modified.js"]');
          if (foxitScript) {
            foxitScript.remove();
          }

          // Remove CSS
          const foxitCss = document.querySelector('link[href="./lib/UIExtension.css"]');
          if (foxitCss) {
            foxitCss.remove();
          }

          // Reset the flag and dispatch event
          window.isFoxitLoaded = {cssLoaded: false, scriptLoaded: false};
          window.dispatchEvent(new Event('foxitStatusChanged'));
          console.log("Foxit resources cleaned up");
        };
      }, []);

    const groupChatContext = useGroupChatContext();

    const { selectedChat, problemsMeta, setScreenWidth, vezoCallData, isPinCodeScreenActive, setIsPinCodeScreenActive, authData, 
            isOpenPatientMedsAllergyPopup, setIsOpenPatientMedsAllergyPopup, isOpenRequestAppointmentPopup, setIsOpenRequestAppointmentPopup, showSettings, openAppt,
            isOpenPatientApptPopup, setIsOpenPatientApptPopup, setOpenAppt, isForceOpen2FaPopup, setForceOpen2FaPopup
        } = useMemo(() => ({
        selectedChat: groupChatContext.selectedChat,
        problemsMeta: groupChatContext.problemsMeta,
        setScreenWidth: groupChatContext.setScreenWidth,
        vezoCallData: groupChatContext.vezoCallData,
        isPinCodeScreenActive: groupChatContext.isPinCodeScreenActive,
        setIsPinCodeScreenActive: groupChatContext.setIsPinCodeScreenActive,
        authData: groupChatContext.authData,
        isOpenPatientMedsAllergyPopup: groupChatContext.isOpenPatientMedsAllergyPopup,
        setIsOpenPatientMedsAllergyPopup: groupChatContext.setIsOpenPatientMedsAllergyPopup,
        isOpenRequestAppointmentPopup: groupChatContext.isOpenRequestAppointmentPopup,
        setIsOpenRequestAppointmentPopup: groupChatContext.setIsOpenRequestAppointmentPopup,
        showFutureAppointments: groupChatContext.showFutureAppointments,
        openAppt: groupChatContext.openAppt,
        showSettings: groupChatContext.showSettings,
        setIsOpenPatientApptPopup: groupChatContext.setIsOpenPatientApptPopup,
        isOpenPatientApptPopup: groupChatContext.isOpenPatientApptPopup,
        setOpenAppt: groupChatContext.setOpenAppt,
        isForceOpen2FaPopup: groupChatContext.isForceOpen2FaPopup,
        setForceOpen2FaPopup: groupChatContext.setForceOpen2FaPopup
    }));

    const [problemReportData, setProblemReportData] = useState({isOpen: false, data: []});
    const [isOpenPatientEncounterPopup, setIsOpenPatientEncounterPopup] = useState(false);
    const [isOpenReferralPopup, setIsOpenReferralPopup] = useState(false);
    const [isOpenPrintMessagePopup, setIsOpenPrintMessagePopup] = useState(false);
    const [isOpenChatbotPopup, setIsOpenChatbotPopup] = useState(false);
    const [letterOrderDialogState, setLetterOrderDialogState] = useState(false);
    const isPatientChatSelected = isPatientChatMessage(selectedChat);
    const selectedLocationId = selectedChat?.location?.id || selectedChat?.location_id;
    const { data: locationConfigurationData } = useGetLocationConfigurationQuery(
        { location_id: selectedLocationId },
        { skip: !selectedLocationId || !isPatientChatSelected }
    );
    const locationConfiguration = locationConfigurationData?.data || {};
    const showPatientInfoSidebar = isPatientChatSelected && (
        isEnabled(locationConfiguration?.show_vezo_session) ||
        isEnabled(locationConfiguration?.show_appointment) ||
        isEnabled(locationConfiguration?.show_medication) ||
        isEnabled(locationConfiguration?.show_allergies)
    );
    const {
        practice_id,
      two_factor_auth_enabled
    } = JSON.parse(localStorage.getItem("auth"));

    useEffect(() => {
         if(two_factor_auth_enabled == false && !isForceOpen2FaPopup){
                  setForceOpen2FaPopup(true);
     }
                         
    },[two_factor_auth_enabled])

    const onSelectHeaderOption = (opt) => {
        if(opt.from == 'actions'){
            if(opt.slug == 'scheduler'){
                setIsOpenPatientApptPopup(true)
            }
            else if(opt.slug == 'allergies_meds'){
                setIsOpenPatientMedsAllergyPopup(true)
            }
            else if(opt.slug == 'referral_form'){
                setIsOpenReferralPopup(true)
            }
            else if(opt.slug == 'print_messages'){
                setIsOpenPrintMessagePopup(true)
            }
            else if(opt.slug == 'patient_encounters'){
                setIsOpenPatientEncounterPopup(true)
            }
            else if (opt.slug=="letter_orders"){
                setLetterOrderDialogState(true)
            }
            else{
                setIsOpenRequestAppointmentPopup(true);
            }
        }
        else{
            if(opt.slug == 'change_status'){
                setIsOpenChatbotPopup(true)
            }
            else{
                setProblemReportData({isOpen: true, title: opt.slug, data: problemsMeta[opt.slug]});
            }
        }
    }

    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        };
  
        // Add event listener for window resize
        window.addEventListener('resize', handleResize);
  
        // Clean up event listener on component unmount
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        const handlePinCodeScreenChange = () => {
            setIsPinCodeScreenActive(JSON.parse(localStorage.getItem('isPinCodeScreenActive')) == null ? false: localStorage.getItem('isPinCodeScreenActive'));
        };

        window.addEventListener("pinCodeScreenStatusChanged", handlePinCodeScreenChange);
        return () => {
            window.removeEventListener("pinCodeScreenStatusChanged", handlePinCodeScreenChange);
        };
    }, []);


    return (
        <>
            {isPinCodeScreenActive ? <PinCodeFourDigit /> : null}
            {isForceOpen2FaPopup ? 
                <MainPageDialog
                    title="Security"
                    isShowCloseBtn = {false}
                    modalSize="md"
                    customHeight={"max-h-[80vh]"}
                    component={<TwoFa 
                        close2FaPopup={() => setForceOpen2FaPopup(false)}
                        isShowCloseBtn = {false}
                    />}
                />
            : null}
            
            {!showSettings ? (
                <div className={isPinCodeScreenActive ? "hidden" : "block"}>
                <HeaderChat />
                <div className="flex" id="chat-container">
                    <ChatSidebar />
                    {selectedChat?.id || (selectedChat?.pid && !selectedChat.message) ? (
                        <div className="flex-1 flex relative flex-col h-[88vh]">
                            <MessageThreadHeader
                            onSelectHeaderOption={onSelectHeaderOption}
                            />
                            <div className="flex flex-1 overflow-auto">
                                <main className="flex relative flex-col bg-blue-50 w-full">
                                    <ChatWindow />
                                    <MessageThreadFooter 
                                        letterOrderDialogState={letterOrderDialogState} 
                                        setLetterOrderDialogState={setLetterOrderDialogState}
                                    />
                                </main>
                                {showPatientInfoSidebar ?
                                    <div className="hidden md:flex xl:inset-y-0 xl:flex xl:w-[350px] bg-chat-bg-color xl:flex-col p-1 overflow-y-auto">
                                        <PatientInfo />
                                    </div>
                                : null}
                            </div>
                        </div>
                    ) : (
                        <NoChatMessage />
                    )}
                </div>
                {problemReportData?.isOpen ? (
                    <MainPageDialog
                    title={problemReportData.title}
                    onCancelHandler={() => setProblemReportData({ isOpen: false })}
                    // modalSize="md"
                    maxHeight={true}
                    customHeight={"min-h-[40vh] overflow-y-auto"}
                    component={
                        <ProblemReportForm
                        problemReport={problemReportData}
                        cancelProblemReportPopup={() =>
                            setProblemReportData({ isOpen: false })
                        }
                        />
                    }
                    footer={false}
                    ></MainPageDialog>
                ) : null}

                {isOpenRequestAppointmentPopup ? (
                    <MainPageDialog
                    title={!authData?.is_provider ? "Request Appointment" : "Schedule Appointment"}
                    onCancelHandler={() => setIsOpenRequestAppointmentPopup(false)}
                    // modalSize="md"
                    maxHeight={true}
                    customHeight={"min-h-[80vh] overflow-y-auto"}
                    component={
                        <AppointmentsFollowUp
                        closeApptPopup={() => setIsOpenRequestAppointmentPopup(false)}
                        />
                    }
                    footer={false}
                    ></MainPageDialog>
                ) : null}

                {isOpenPatientApptPopup ? (
                    <MainPageDialog
                    title="Patient Appointments"
                    onCancelHandler={() => setIsOpenPatientApptPopup(false)}
                    // modalSize="md"
                    maxHeight={true}
                    customHeight={"min-h-[80vh] overflow-y-auto"}
                    component={
                        <LocationAppointment
                        pid={selectedChat.pid}
                        closeLocationApptPopup={() =>
                            setIsOpenPatientApptPopup(false)
                        }
                        />
                    }
                    footer={false}
                    ></MainPageDialog>
                ) : null}

                {isOpenPatientEncounterPopup ? (
                    <MainPageDialog
                    title="Patient Encounters"
                    onCancelHandler={() => setIsOpenPatientEncounterPopup(false)}
                    maxHeight={true}
                    customHeight={"min-h-[80vh] overflow-y-auto"}
                    component={
                        <EncounterList
                        closeEncounterPopup={() =>
                            setIsOpenPatientEncounterPopup(false)
                        }
                        />
                    }
                    footer={false}
                    ></MainPageDialog>
                ) : null}

                {isOpenPatientMedsAllergyPopup ? (
                    <MainPageDialog
                    title="Allergies & Medications"
                    onCancelHandler={() => setIsOpenPatientMedsAllergyPopup(false)}
                    // modalSize="md"
                    maxHeight={true}
                    customHeight={"min-h-[80vh] overflow-y-auto"}
                    component={
                        <MedicationAllergy
                        closeMedsAllergyPopup={() =>
                            setIsOpenPatientMedsAllergyPopup(false)
                        }
                        />
                    }
                    footer={false}
                    ></MainPageDialog>
                ) : null}

                {isOpenChatbotPopup ? (
                    <MainPageDialog
                    title="Change Status"
                    onCancelHandler={() => setIsOpenChatbotPopup(false)}
                    modalSize="md"
                    maxHeight={true}
                    customHeight={"min-h-[28vh] max-h-[90vh] overflow-hidden"}
                    component={
                        <ChatbotPopup
                        closeChatbotPopup={() => setIsOpenChatbotPopup(false)}
                        />
                    }
                    footer={false}
                    ></MainPageDialog>
                ) : null}

                {isOpenReferralPopup ? (
                    <MainPageDialog
                    title="Incoming Referral"
                    onCancelHandler={() => setIsOpenReferralPopup(false)}
                    modalSize="md"
                    maxHeight={true}
                    // customHeight={"min-h-[40vh] overflow-y-auto"}
                    component={
                        <IncomingReferral
                        cancelReferralPopup={() => setIsOpenReferralPopup(false)}
                        />
                    }
                    footer={false}
                    ></MainPageDialog>
                ) : null}

                {/* <MovableMicIcon /> */}
                {vezoCallData?.show ? <VezoCallWindow /> : null}

                {isOpenPrintMessagePopup ? (
                    <MainPageDialog
                    title="Print Messages"
                    onCancelHandler={() => setIsOpenPrintMessagePopup(false)}
                    modalSize="md"
                    maxHeight={true}
                    customHeight={"min-h-[50vh] overflow-y-auto"}
                    component={
                        <PrintMessage
                        cancelPrintPopup={() => setIsOpenPrintMessagePopup(false)}
                        />
                    }
                    footer={false}
                    ></MainPageDialog>
                ) : null}

                {openAppt.isOpen ? (
                    <MainPageDialog
                    title="Edit Appointment"
                    onCancelHandler={() => setOpenAppt({ isOpen: false })}
                    // modalSize="md"
                    maxHeight={true}
                    customHeight={"min-h-[80vh] overflow-y-auto"}
                    component={
                        <AppointmentsFollowUp
                        apptId={openAppt.id}
                        closeApptPopup={() => setOpenAppt({ isOpen: false })}
                        />
                    }
                    footer={false}
                    ></MainPageDialog>
                ) : null}
                </div>
            ) : !isPinCodeScreenActive ? <SettingsPage /> : null}
        </>
    );
};

export default GroupChat;